// src/components/ArtistMarketRatioTool.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useTranslation } from "react-i18next";

function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function normalizeKey(value) {
  // IMPORTANT : artist_key en base utilise des underscores (ex: "bernar_venet").
  // Cette fonction doit produire le meme format pour que eq("artist_key", ...) trouve les lignes.
  return normalizeText(value)
    .replace(/[‘’']/g, "")
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

function toNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(String(value).replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function formatEuro(value, locale) {
  const n = toNumber(value);
  if (n === null) return "—";
  return new Intl.NumberFormat(locale || undefined, { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Math.round(n));
}

function median(values) {
  const clean = values.filter((v) => Number.isFinite(v)).sort((a, b) => a - b);
  if (!clean.length) return null;
  const mid = Math.floor(clean.length / 2);
  return clean.length % 2 ? clean[mid] : (clean[mid - 1] + clean[mid]) / 2;
}

function getSoldPrice(row) {
  // Règle dure : jamais estimated_value comme vente réalisée.
  return toNumber(row?.hammer_price ?? row?.price_with_premium ?? row?.buyer_premium_price ?? row?.premium_price);
}

function isUnsold(row) {
  const text = normalizeText(`${row?.comment || ""} ${row?.status || ""} ${row?.result || ""}`);
  return text.includes("not sold") || text.includes("invendu") || text.includes("unsold");
}

function inferMediumBucket(value) {
  const text = normalizeText(value);
  if (/oil|huile|acrylic|acrylique|canvas|toile|painting|peinture/.test(text)) return "painting";
  if (/watercolor|aquarelle|gouache|ink|encre|pencil|crayon|wash|lavis|paper|papier|drawing|dessin/.test(text)) return "work_on_paper";
  if (/print|lithograph|screenprint|serigraph|estampe|gravure|etching|aquatint|edition/.test(text)) return "print";
  if (/sculpture|bronze|ceramic|ceramique|terracotta|resin|bois|wood|metal|fer|forge|iron|steel|acier|fonte|inox|cuivre|laiton|aluminium|wire|fil|soud|weld|fonte|fonte/.test(text)) return "sculpture_object";
  return "other";
}

function inferSizeBucket(row) {
  const h = toNumber(row?.height_cm);
  const w = toNumber(row?.width_cm);
  if (!h || !w) return "unknown";
  const area = h * w;
  if (area >= 20000) return "large";
  if (area >= 5000) return "medium";
  return "small";
}


function extractAnalogArtistsFromQuote(quote) {
  const names = [];

  const pushName = (value) => {
    if (!value) return;
    if (typeof value === "string") {
      value
        .split(/[;,|]/)
        .map((v) => v.trim())
        .filter(Boolean)
        .forEach((v) => names.push(v));
      return;
    }
    if (Array.isArray(value)) {
      value.forEach(pushName);
      return;
    }
    if (typeof value === "object") {
      pushName(value.artist || value.name || value.artist_name || value.label || value.value);
    }
  };

  pushName(quote?.analog_artists);
  pushName(quote?.analog_comparables);
  pushName(quote?.analogue_artists);
  pushName(quote?.analogue_comparables);
  pushName(quote?.comparable_artists);
  pushName(quote?.market_analog_artists);

  return [...new Set(names.map((name) => String(name || "").trim()).filter(Boolean))];
}

function relationScore(row, mediumBucket, sizeBucket) {
  let score = 0;
  if (inferMediumBucket(row?.medium || row?.material) === mediumBucket) score += 60;
  if (sizeBucket === "all" || inferSizeBucket(row) === sizeBucket || inferSizeBucket(row) === "unknown") score += 25;
  if (row?.auction_date) score += 15;
  return score;
}

export default function ArtistMarketRatioTool({
  artwork,
  currentUserId,
  disabled = false,
  defaultPivotArtist = "",
  defaultTargetArtist = "",
  artistQuote = null,
  estimationId = null,
  onApplyExtrapolatedComparable,
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language || undefined;
  const artworkArtist = artwork?.artist || artwork?.artist_name || "";
  const artworkMedium = artwork?.medium || artwork?.medium_original || artwork?.specifications?.medium || "";
  const analogArtistOptions = useMemo(() => extractAnalogArtistsFromQuote(artistQuote), [artistQuote]);

  const [pivotArtist, setPivotArtist] = useState(defaultPivotArtist || analogArtistOptions[0] || "");
  const [targetArtist, setTargetArtist] = useState(defaultTargetArtist || artworkArtist || "Claire Tabouret");
  const [mediumBucket, setMediumBucket] = useState(inferMediumBucket(artworkMedium));
  const [sizeBucket, setSizeBucket] = useState("all");
  const [periodStart, setPeriodStart] = useState("2020-01-01");
  const [periodEnd, setPeriodEnd] = useState(new Date().toISOString().slice(0, 10));
  const [cautionFactor, setCautionFactor] = useState(0.65);
  const [referencePrice, setReferencePrice] = useState("");
  const [pivotRows, setPivotRows] = useState([]);
  const [targetRows, setTargetRows] = useState([]);
  const [selectedPivotIds, setSelectedPivotIds] = useState(new Set());
  const [selectedTargetIds, setSelectedTargetIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastSavedRatioId, setLastSavedRatioId] = useState(null);

  useEffect(() => {
    if (!pivotArtist && analogArtistOptions.length) setPivotArtist(analogArtistOptions[0]);
  }, [analogArtistOptions, pivotArtist]);

  async function fetchRows() {
    setLoading(true);
    setError("");
    setLastSavedRatioId(null);

    const baseSelect = "id,title,artist,artist_key,medium,material,dimensions,height_cm,width_cm,auction_house,auction_date,hammer_price,estimate_low,estimate_high,price_currency,comment,validated,source,source_file,source_page";
    const filters = (query) => {
      let q = query.select(baseSelect).not("hammer_price", "is", null).gte("auction_date", periodStart).lte("auction_date", periodEnd).limit(80);
      return q;
    };

    if (!pivotArtist || !targetArtist) {
      setError(t("round36.artistMarketRatio.errors.selectArtists"));
      setPivotRows([]);
      setTargetRows([]);
      setLoading(false);
      return;
    }

    const pivotKey = normalizeKey(pivotArtist);
    const targetKey = normalizeKey(targetArtist);

    const [pivotRes, targetRes] = await Promise.all([
      filters(supabase.from("estimation_comparables")).eq("artist_key", pivotKey),
      filters(supabase.from("estimation_comparables")).eq("artist_key", targetKey),
    ]);

    if (pivotRes.error || targetRes.error) {
      setError(pivotRes.error?.message || targetRes.error?.message || t("round36.artistMarketRatio.errors.supabase"));
      setPivotRows([]);
      setTargetRows([]);
      setLoading(false);
      return;
    }

    const clean = (rows) => (rows || [])
      .filter((r) => !isUnsold(r) && getSoldPrice(r) !== null)
      .map((r) => ({ ...r, matchScore: relationScore(r, mediumBucket, sizeBucket) }))
      // matchScore sert au tri uniquement, on ne filtre plus par seuil
      .sort((a, b) => b.matchScore - a.matchScore || new Date(b.auction_date || 0) - new Date(a.auction_date || 0));

    const p = clean(pivotRes.data);
    const tgt = clean(targetRes.data);
    setPivotRows(p);
    setTargetRows(tgt);
    setSelectedPivotIds(new Set(p.slice(0, 8).map((r) => r.id)));
    setSelectedTargetIds(new Set(tgt.slice(0, 8).map((r) => r.id)));
    setLoading(false);
  }

  const selectedPivotRows = useMemo(() => pivotRows.filter((r) => selectedPivotIds.has(r.id)), [pivotRows, selectedPivotIds]);
  const selectedTargetRows = useMemo(() => targetRows.filter((r) => selectedTargetIds.has(r.id)), [targetRows, selectedTargetIds]);

  const pivotMedian = useMemo(() => median(selectedPivotRows.map(getSoldPrice)), [selectedPivotRows]);
  const targetMedian = useMemo(() => median(selectedTargetRows.map(getSoldPrice)), [selectedTargetRows]);
  const marketRatio = pivotMedian && targetMedian ? targetMedian / pivotMedian : null;
  const extrapolatedValue = marketRatio && toNumber(referencePrice)
    ? Math.round(toNumber(referencePrice) * marketRatio * toNumber(cautionFactor))
    : null;

  useEffect(() => {
    if (!referencePrice && pivotMedian) setReferencePrice(String(Math.round(pivotMedian)));
  }, [pivotMedian, referencePrice]);

  function toggle(setter, ids, id) {
    const next = new Set(ids);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setter(next);
  }

  async function saveRatio() {
    if (!marketRatio || !pivotMedian || !targetMedian) return null;
    const payload = {
      pivot_artist_key: normalizeKey(pivotArtist),
      pivot_artist: pivotArtist,
      target_artist_key: normalizeKey(targetArtist),
      target_artist: targetArtist,
      medium_bucket: mediumBucket,
      size_bucket: sizeBucket,
      period_start: periodStart,
      period_end: periodEnd,
      pivot_median: Math.round(pivotMedian),
      target_median: Math.round(targetMedian),
      market_ratio: Number(marketRatio.toFixed(4)),
      caution_factor: toNumber(cautionFactor) || 0.65,
      sample_size_pivot: selectedPivotRows.length,
      sample_size_target: selectedTargetRows.length,
      pivot_comparable_ids: selectedPivotRows.map((r) => r.id),
      target_comparable_ids: selectedTargetRows.map((r) => r.id),
      created_by: currentUserId || null,
      validated: false,
      comment: t("round36.artistMarketRatio.savedComment", { pivot: selectedPivotRows.length, target: selectedTargetRows.length }),
    };

    const { data, error: insertError } = await supabase
      .from("artist_market_ratios")
      .insert(payload)
      .select("id")
      .single();

    if (insertError) {
      setError(insertError.message);
      return null;
    }
    setLastSavedRatioId(data?.id || null);
    return data?.id || null;
  }

  async function applyExtrapolation() {
    if (!extrapolatedValue) return;
    const ratioId = lastSavedRatioId || await saveRatio();
    const syntheticComparable = {
      title: `Extrapolation ${targetArtist} via ${pivotArtist}`,
      artist: targetArtist,
      source: "artist_market_ratio",
      price: extrapolatedValue,
      hammer_price: extrapolatedValue,
      estimated_value: extrapolatedValue,
      date: periodEnd,
      auction_date: periodEnd,
      medium: mediumBucket,
      comment: t("round36.artistMarketRatio.extrapolationComment", { price: formatEuro(referencePrice, locale), ratio: marketRatio?.toFixed(2), caution: cautionFactor, ratioId: ratioId || t("round36.artistMarketRatio.notSaved") }),
      ratio_id: ratioId,
      source_type: "externe",
      // Marqueur critique : ce comparable vient d'un ratio pivot (autre artiste/marché).
      // Sans ce flag, isOwnTechnicalSegment() le traite comme un résultat propre
      // et efface la décote prudentielle Groupe B — ce qui serait une erreur méthodologique.
      is_analog_comparable: true,
      market_segment: "analog",
    };
    if (estimationId && currentUserId) {
      const { error: decisionError } = await supabase
        .from("estimation_selected_comparables")
        .insert({
          estimation_id: estimationId,
          ratio_id: ratioId || null,
          kind: "extrapolation",
          selected_price: extrapolatedValue,
          weight: 1,
          note: syntheticComparable.comment,
          created_by: currentUserId,
          artist_snapshot: targetArtist || syntheticComparable.artist || null,
          title_snapshot: syntheticComparable.title || null,
          source_snapshot: syntheticComparable.source || "artist_market_ratio",
        });

      if (decisionError) {
        setError(decisionError.message);
        return;
      }
    }

    onApplyExtrapolatedComparable?.(syntheticComparable, {
      ratioId,
      pivotMedian,
      targetMedian,
      marketRatio,
      cautionFactor: toNumber(cautionFactor),
      extrapolatedValue,
    });
  }

  const enoughData = selectedPivotRows.length >= 2 && selectedTargetRows.length >= 2;

  const Row = ({ row, selected, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left border rounded-lg p-3 transition ${selected ? "border-black bg-gray-50" : "border-gray-200 bg-white hover:bg-gray-50"}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-gray-900">{row.title || t("round36.artistMarketRatio.untitled")}</p>
          <p className="text-xs text-gray-500">{row.auction_house || t("round36.artistMarketRatio.unknownHouse")} · {row.auction_date || t("round36.artistMarketRatio.unknownDate")}</p>
          <p className="text-[11px] text-gray-400">{row.medium || row.material || t("round36.artistMarketRatio.unknownMedium")} · {row.dimensions || t("round36.artistMarketRatio.unknownDimensions")}</p>
        </div>
        <div className="text-right">
          <p className="text-sm font-semibold text-gray-900">{formatEuro(getSoldPrice(row), locale)}</p>
          <p className="text-[11px] text-gray-500">{t("round36.artistMarketRatio.match", { score: row.matchScore })}</p>
        </div>
      </div>
    </button>
  );

  return (
    <div className="border border-indigo-200 bg-indigo-50 rounded-xl p-4 mb-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-sm font-semibold text-indigo-950">{t("round36.artistMarketRatio.title")}</p>
          <p className="text-xs text-indigo-800 mt-1">
            {t("round36.artistMarketRatio.description")}
          </p>
        </div>
        <button
          type="button"
          onClick={fetchRows}
          disabled={disabled || loading}
          className="px-4 py-2 rounded bg-indigo-950 text-white text-xs disabled:bg-gray-300"
        >
          {loading ? t("round36.artistMarketRatio.actions.calculating") : t("round36.artistMarketRatio.actions.loadSales")}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
        <label className="text-xs text-gray-700">{t("round36.artistMarketRatio.fields.pivotArtist")}
          {analogArtistOptions.length > 0 ? (
            <select
              value={pivotArtist}
              onChange={(e) => setPivotArtist(e.target.value)}
              className="mt-1 w-full border rounded px-2 py-1.5 text-sm bg-white"
            >
              <option value="">{t("round36.artistMarketRatio.fields.chooseValidatedAnalog")}</option>
              {analogArtistOptions.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
              <option value="__manual__">{t("round36.artistMarketRatio.fields.otherArtist")}</option>
            </select>
          ) : (
            <input value={pivotArtist} onChange={(e) => setPivotArtist(e.target.value)} className="mt-1 w-full border rounded px-2 py-1.5 text-sm" placeholder={t("round36.artistMarketRatio.fields.noAnalogPlaceholder")} />
          )}
          {pivotArtist === "__manual__" && (
            <input autoFocus value="" onChange={(e) => setPivotArtist(e.target.value)} className="mt-2 w-full border rounded px-2 py-1.5 text-sm" placeholder={t("round36.artistMarketRatio.fields.pivotPlaceholder")} />
          )}
          {analogArtistOptions.length > 0 && pivotArtist !== "__manual__" && (
            <p className="mt-1 text-[10px] text-indigo-700">{t("round36.artistMarketRatio.fields.analogSource")}</p>
          )}
        </label>
        <label className="text-xs text-gray-700">{t("round36.artistMarketRatio.fields.targetArtist")}
          <input value={targetArtist} onChange={(e) => setTargetArtist(e.target.value)} className="mt-1 w-full border rounded px-2 py-1.5 text-sm" />
        </label>
        <label className="text-xs text-gray-700">{t("round36.artistMarketRatio.fields.mediumBucket")}
          <select value={mediumBucket} onChange={(e) => setMediumBucket(e.target.value)} className="mt-1 w-full border rounded px-2 py-1.5 text-sm">
            <option value="painting">{t("round36.artistMarketRatio.medium.painting")}</option>
            <option value="work_on_paper">{t("round36.artistMarketRatio.medium.workOnPaper")}</option>
            <option value="print">{t("round36.artistMarketRatio.medium.print")}</option>
            <option value="sculpture_object">{t("round36.artistMarketRatio.medium.sculptureObject")}</option>
            <option value="other">{t("round36.artistMarketRatio.medium.other")}</option>
          </select>
        </label>
        <label className="text-xs text-gray-700">{t("round36.artistMarketRatio.fields.format")}
          <select value={sizeBucket} onChange={(e) => setSizeBucket(e.target.value)} className="mt-1 w-full border rounded px-2 py-1.5 text-sm">
            <option value="all">{t("round36.artistMarketRatio.size.all")}</option>
            <option value="small">{t("round36.artistMarketRatio.size.small")}</option>
            <option value="medium">{t("round36.artistMarketRatio.size.medium")}</option>
            <option value="large">{t("round36.artistMarketRatio.size.large")}</option>
          </select>
        </label>
        <label className="text-xs text-gray-700">{t("round36.artistMarketRatio.fields.caution")}
          <input type="number" step="0.05" min="0.1" max="1" value={cautionFactor} onChange={(e) => setCautionFactor(e.target.value)} className="mt-1 w-full border rounded px-2 py-1.5 text-sm" />
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <label className="text-xs text-gray-700">{t("round36.artistMarketRatio.fields.periodStart")}
          <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className="mt-1 w-full border rounded px-2 py-1.5 text-sm" />
        </label>
        <label className="text-xs text-gray-700">{t("round36.artistMarketRatio.fields.periodEnd")}
          <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className="mt-1 w-full border rounded px-2 py-1.5 text-sm" />
        </label>
        <label className="text-xs text-gray-700">{t("round36.artistMarketRatio.fields.referenceSale")}
          <input type="number" step="100" min="0" value={referencePrice} onChange={(e) => setReferencePrice(e.target.value)} className="mt-1 w-full border rounded px-2 py-1.5 text-sm" placeholder={t("round36.artistMarketRatio.fields.referencePlaceholder")} />
        </label>
        <div className="rounded-lg bg-white border border-indigo-100 p-3">
          <p className="text-[11px] text-gray-500">{t("round36.artistMarketRatio.result")}</p>
          <p className="text-lg font-semibold text-gray-950">{formatEuro(extrapolatedValue, locale)}</p>
          {marketRatio && <p className="text-[11px] text-gray-500">{t("round36.artistMarketRatio.ratioCaution", { ratio: marketRatio.toFixed(2), caution: cautionFactor })}</p>}
        </div>
      </div>

      {error && <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-3">{error}</p>}
      {!estimationId && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3">{t("round36.artistMarketRatio.pendingEstimation")}</p>}

      {(pivotRows.length || targetRows.length) ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-700">{t("round36.artistMarketRatio.pivotMedian", { value: formatEuro(pivotMedian, locale) })}</p>
              <p className="text-[11px] text-gray-500">{selectedPivotRows.length}/{pivotRows.length}</p>
            </div>
            <div className="space-y-2 max-h-72 overflow-auto pr-1">
              {pivotRows.map((row) => <Row key={row.id} row={row} selected={selectedPivotIds.has(row.id)} onClick={() => toggle(setSelectedPivotIds, selectedPivotIds, row.id)} />)}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-700">{t("round36.artistMarketRatio.targetMedian", { value: formatEuro(targetMedian, locale) })}</p>
              <p className="text-[11px] text-gray-500">{selectedTargetRows.length}/{targetRows.length}</p>
            </div>
            <div className="space-y-2 max-h-72 overflow-auto pr-1">
              {targetRows.map((row) => <Row key={row.id} row={row} selected={selectedTargetIds.has(row.id)} onClick={() => toggle(setSelectedTargetIds, selectedTargetIds, row.id)} />)}
            </div>
          </div>
        </div>
      ) : null}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 bg-white border border-indigo-100 rounded-lg p-3">
        <p className="text-xs text-gray-700">
          {enoughData
            ? t("round36.artistMarketRatio.enoughData")
            : t("round36.artistMarketRatio.notEnoughData")}
        </p>
        <div className="flex gap-2">
          <button type="button" onClick={saveRatio} disabled={!marketRatio || disabled} className="px-4 py-2 rounded border border-indigo-900 text-indigo-950 text-xs disabled:text-gray-300 disabled:border-gray-200">
            {t("round36.artistMarketRatio.actions.saveRatio")}
          </button>
          <button type="button" onClick={applyExtrapolation} disabled={!extrapolatedValue || disabled} className="px-4 py-2 rounded bg-black text-white text-xs disabled:bg-gray-300">
            {t("round36.artistMarketRatio.actions.addToEstimation")}
          </button>
        </div>
      </div>
    </div>
  );
}